import { AlertTriangle, CheckCircle2, ChevronRight, RefreshCw, Square } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ControlRoomSnapshot, UiHealth } from './api';
import type { RebuildRouteId } from './navigation';
import { stopObsStream } from './stream-api';
import './control-room.css';

type AttentionItem = {
  id: string;
  label: string;
  detail: string;
  tone: 'warning' | 'bad';
  route: RebuildRouteId;
};

type ControlRoomPageProps = {
  snapshot: ControlRoomSnapshot | null;
  error: string;
  loading: boolean;
  onRefresh: () => void;
};

function count(value: number | null): string {
  return value === null ? '—' : value.toLocaleString('en-GB');
}

function healthLabel(value: UiHealth): string {
  if (value === 'online') return 'Online';
  if (value === 'degraded') return 'Check';
  if (value === 'offline') return 'Offline';
  if (value === 'disabled') return 'Off';
  return 'Unknown';
}

function shortTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function attention(snapshot: ControlRoomSnapshot): AttentionItem[] {
  const items: AttentionItem[] = [];

  if (snapshot.system.status === 'offline') {
    items.push({ id: 'system', label: 'FFSControl', detail: 'Server health is offline.', tone: 'bad', route: 'system-health' });
  } else if (snapshot.system.status !== 'online') {
    items.push({ id: 'system', label: 'FFSControl', detail: 'Server health needs checking.', tone: 'warning', route: 'system-health' });
  }

  if (snapshot.system.database === 'offline') {
    items.push({ id: 'database', label: 'Database', detail: 'The FFSControl database is offline.', tone: 'bad', route: 'system-health' });
  } else if (snapshot.system.database !== 'online') {
    items.push({ id: 'database', label: 'Database', detail: 'Database health needs checking.', tone: 'warning', route: 'system-health' });
  }

  if (!snapshot.production.agentConnected) {
    items.push({ id: 'agent', label: 'Stream PC', detail: 'The Stream PC Agent is not connected.', tone: 'bad', route: 'system-health' });
  }

  if (snapshot.production.obsStatus === 'offline') {
    items.push({ id: 'obs', label: 'OBS', detail: 'OBS is not available to FFSControl.', tone: 'bad', route: 'obs' });
  } else if (snapshot.production.obsStatus !== 'online') {
    items.push({ id: 'obs', label: 'OBS', detail: 'OBS needs checking.', tone: 'warning', route: 'obs' });
  }

  if (snapshot.twitch.live && (snapshot.production.soundboardStatus !== 'online' || snapshot.production.soundboardClients < 1)) {
    items.push({
      id: 'stream-audio',
      label: 'Stream audio',
      detail: snapshot.production.soundboardStatus !== 'online'
        ? 'FFSControl stream audio is not online.'
        : 'No FFSControl audio browser client is connected.',
      tone: 'bad',
      route: 'sounds',
    });
  }

  if (!snapshot.wam.agentConnected || snapshot.wam.status === 'offline') {
    items.push({ id: 'wam', label: 'WhatsAMusic', detail: 'The WAM route is not fully connected.', tone: 'bad', route: 'wam' });
  } else if (snapshot.wam.status !== 'online') {
    items.push({ id: 'wam', label: 'WhatsAMusic', detail: 'WAM needs checking.', tone: 'warning', route: 'wam' });
  }

  if (snapshot.bot.status === 'offline') {
    items.push({ id: 'bot', label: 'Chat Bot', detail: 'The FFSControl chat bot is offline.', tone: 'bad', route: 'chat-bot' });
  } else if (snapshot.bot.status === 'degraded') {
    items.push({ id: 'bot', label: 'Chat Bot', detail: 'The chat bot connection needs checking.', tone: 'warning', route: 'chat-bot' });
  }

  return items;
}

export function ControlRoomPage({ snapshot, error, loading, onRefresh }: ControlRoomPageProps) {
  const attentionItems = useMemo(() => (snapshot ? attention(snapshot) : []), [snapshot]);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState('');

  async function emergencyStop() {
    if (!snapshot?.production.streaming || actionBusy) return;
    if (!window.confirm('END THE LIVE STREAM NOW?\n\nThis tells OBS to stop streaming immediately. It does not change scenes or close OBS.')) return;
    setActionBusy(true);
    setActionMessage('');
    try {
      await stopObsStream();
      setActionMessage('OBS live stream stopped.');
      onRefresh();
    } catch (caught) {
      setActionMessage(caught instanceof Error ? caught.message : 'The live stream could not be stopped.');
    } finally {
      setActionBusy(false);
    }
  }

  if (loading && !snapshot) {
    return <div className="ffs-next-loading">Loading current stream state…</div>;
  }

  return (
    <div className="ffs-next-control-room">
      <div className="ffs-next-control-actions">
        <div>
          <strong>{snapshot?.twitch.live ? 'Stream is live' : 'Stream is offline'}</strong>
          <span>{snapshot?.twitch.live ? snapshot.twitch.title || 'No stream title' : 'Nothing is currently broadcasting.'}</span>
        </div>
        <div className="ffs-next-control-action-buttons">
          {snapshot?.production.streaming ? (
            <button type="button" className="danger" disabled={actionBusy} onClick={() => void emergencyStop()}>
              <Square size={14} /> End Live Stream Now
            </button>
          ) : null}
          <button type="button" onClick={onRefresh} disabled={actionBusy}>
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </div>

      {error ? <div className="ffs-next-error">{error}</div> : null}
      {actionMessage ? <div className="ffs-next-action-message">{actionMessage}</div> : null}

      {snapshot ? (
        <>
          <section className="ffs-next-now-grid" aria-label="Current stream state">
            <article>
              <small>Stream</small>
              <strong className={snapshot.twitch.live ? 'good' : ''}>{snapshot.twitch.live ? 'LIVE' : 'OFFLINE'}</strong>
              <span>{snapshot.twitch.category || 'No Twitch category'}</span>
            </article>
            <article>
              <small>OBS</small>
              <strong>{snapshot.production.scene || healthLabel(snapshot.production.obsStatus)}</strong>
              <span>{snapshot.production.recording ? 'Recording' : 'Not recording'}</span>
            </article>
            <article>
              <small>WhatsAMusic</small>
              <strong>{snapshot.wam.category || 'No category selected'}</strong>
              <span>{healthLabel(snapshot.wam.status)}</span>
            </article>
            <article>
              <small>Now playing</small>
              <strong>{snapshot.production.nowPlaying || 'Nothing playing'}</strong>
              <span>{healthLabel(snapshot.production.soundboardStatus)}</span>
            </article>
          </section>

          <section className="ffs-next-operations-card" aria-label="Operational status">
            <header><div><small>OPERATIONAL STATUS</small><h2>Connections &amp; services</h2></div><span>Live system overview</span></header>
            <div className="ffs-next-operations-grid">
              {[
                { name: 'FFSControl', state: snapshot.system.status, detail: `v${snapshot.system.version}`, route: 'system-health' },
                { name: 'Database', state: snapshot.system.database, detail: 'Persistent data', route: 'system-health' },
                { name: 'Stream PC', state: snapshot.production.agentConnected ? 'online' : 'offline', detail: snapshot.production.agentVersion || 'Agent not connected', route: 'system-health' },
                { name: 'OBS', state: snapshot.production.obsStatus, detail: snapshot.production.scene || 'No scene', route: 'obs' },
                { name: 'Stream audio', state: snapshot.production.soundboardStatus, detail: `${snapshot.production.soundboardClients} browser client(s)`, route: 'sounds' },
                { name: 'Chat Bot', state: snapshot.bot.status, detail: 'Twitch chat connection', route: 'chat-bot' },
                { name: 'WhatsAMusic', state: snapshot.wam.status, detail: snapshot.wam.agentConnected ? 'Agent connected' : 'Agent unavailable', route: 'wam' },
              ].map((service) => (
                <a key={service.name} href={`#/${service.route}`} className="ffs-next-operation">
                  <div><strong>{service.name}</strong><span>{service.detail}</span></div>
                  <span className={`ffs-next-operation-state ${service.state}`}>{healthLabel(service.state as UiHealth)}</span>
                </a>
              ))}
            </div>
          </section>

          <section className="ffs-next-control-grid">
            <article className="ffs-next-card ffs-next-attention-card">
              <header>
                <div>
                  <small>CHECK THIS FIRST</small>
                  <h2>Needs attention</h2>
                </div>
                {attentionItems.length === 0 ? <CheckCircle2 size={20} /> : <AlertTriangle size={20} />}
              </header>
              <div className="ffs-next-attention-list">
                {attentionItems.length === 0 ? (
                  <div className="ffs-next-all-good">
                    <CheckCircle2 size={18} />
                    <div><strong>No obvious problems</strong><span>The main live systems currently look healthy.</span></div>
                  </div>
                ) : (
                  attentionItems.map((item) => (
                    <a className={`ffs-next-attention-row ${item.tone}`} key={item.id} href={`#/${item.route}`}>
                      <div><strong>{item.label}</strong><span>{item.detail}</span></div>
                      <ChevronRight size={15} />
                    </a>
                  ))
                )}
              </div>
            </article>

            <article className="ffs-next-card ffs-next-audience-card">
              <header><div><small>TWITCH</small><h2>Audience</h2></div></header>
              <dl>
                <div><dt>Viewers</dt><dd>{count(snapshot.twitch.viewers)}</dd></div>
                <div><dt>Subscribers</dt><dd>{count(snapshot.twitch.subscribers)}</dd></div>
                <div><dt>Followers</dt><dd>{count(snapshot.twitch.followers)}</dd></div>
              </dl>
            </article>

            <article className="ffs-next-card ffs-next-chat-card">
              <header><div><small>TWITCH CHAT</small><h2>Recent chat</h2></div><span>{healthLabel(snapshot.bot.status)}</span></header>
              <div className="ffs-next-chat-list">
                {snapshot.bot.recentChat.length === 0 ? (
                  <div className="ffs-next-empty">No recent chat messages.</div>
                ) : (
                  snapshot.bot.recentChat.slice(-12).reverse().map((message) => (
                    <div className="ffs-next-chat-row" key={message.id}>
                      <time>{shortTime(message.sentAt)}</time>
                      <strong>{message.userName}</strong>
                      <span>{message.text}</span>
                    </div>
                  ))
                )}
              </div>
            </article>
          </section>
        </>
      ) : null}
    </div>
  );
}
